import type { KpiDefinition } from "../api/types.js";

/**
 * Pure grouping/ordering helpers for the KPI Library's Collection → Category →
 * KPI hierarchy. Taxonomy is platform metadata the API joins onto system Types
 * at read time; a Type with none (tenant-authored, a compatibility shadow, or a
 * pack no longer installed) falls into the synthetic Custom / Uncategorized
 * buckets below so it is still browsable rather than invisible.
 */

export const CUSTOM_COLLECTION_CODE = "__custom";
export const UNCATEGORIZED_CATEGORY_CODE = "__uncategorized";

const CUSTOM_COLLECTION_NAME = "Custom";
const UNCATEGORIZED_CATEGORY_NAME = "Uncategorized";

export interface KpiTaxonomyNode {
  readonly code: string;
  readonly name: string;
  /** Curated display position; null sorts after every declared order. */
  readonly order: number | null;
}

export interface KpiCategoryGroup {
  readonly category: KpiTaxonomyNode;
  readonly definitions: readonly KpiDefinition[];
}

export interface KpiCollectionGroup {
  readonly collection: KpiTaxonomyNode;
  readonly categories: readonly KpiCategoryGroup[];
  readonly count: number;
}

export function collectionNodeOf(definition: KpiDefinition): KpiTaxonomyNode {
  const collection = definition.collection;
  return collection
    ? { code: collection.code, name: collection.name, order: collection.order }
    : {
        code: CUSTOM_COLLECTION_CODE,
        name: CUSTOM_COLLECTION_NAME,
        order: null,
      };
}

export function categoryNodeOf(definition: KpiDefinition): KpiTaxonomyNode {
  const category = definition.category;
  return category
    ? { code: category.code, name: category.name, order: category.order }
    : {
        code: UNCATEGORIZED_CATEGORY_CODE,
        name: UNCATEGORIZED_CATEGORY_NAME,
        order: null,
      };
}

/**
 * Curated `order` first (ascending, absent last), then name, then code. The
 * final tiebreak keeps the list stable when a manifest ships two same-named
 * entries — JSON key order is deliberately never consulted.
 */
export function compareTaxonomyNodes(
  a: KpiTaxonomyNode,
  b: KpiTaxonomyNode,
): number {
  if (a.order !== b.order) {
    if (a.order === null) return 1;
    if (b.order === null) return -1;
    return a.order - b.order;
  }
  return a.name.localeCompare(b.name) || a.code.localeCompare(b.code);
}

/** Search spans the KPI's own text AND its taxonomy names, so "automotive" or
 * "sustainability" finds the KPIs filed under them.
 *
 * The needle is normalized HERE — trimmed and lower-cased — rather than by the
 * caller. It matters because only one side of the comparison used to be
 * normalized inside this function: the haystack was lower-cased, the needle was
 * taken as given, so `matchesKpiSearch(def, "Takt")` answered `false` while
 * `"takt"` answered `true`. The one production caller happened to normalize
 * first, which made the asymmetry invisible in the app and left it as a trap for
 * the next caller (and for every direct unit test). A predicate that lower-cases
 * one side owns lower-casing both.
 *
 * `toLowerCase`, never `toLocaleLowerCase`: the locale-sensitive form follows
 * the HOST's locale, and under Turkish casing `"KPI"` lowers to dotless `kpı` —
 * so a user typing "kpi" stops finding the very things this catalog is made of.
 * Matching must not change with the machine it runs on. */
export function matchesKpiSearch(
  definition: KpiDefinition,
  needle: string,
): boolean {
  const normalized = needle.trim().toLowerCase();
  if (!normalized) return true;
  const haystack = [
    definition.name,
    definition.description,
    definition.collection?.name ?? "",
    definition.category?.name ?? "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(normalized);
}

/** Groups into Collection → Category, ordered by curated metadata. Empty groups
 * cannot occur: a group exists only because a definition landed in it, which is
 * what makes filtered results hide empty headings for free. */
export function groupKpiDefinitions(
  definitions: readonly KpiDefinition[],
): KpiCollectionGroup[] {
  const collections = new Map<
    string,
    {
      node: KpiTaxonomyNode;
      categories: Map<
        string,
        { node: KpiTaxonomyNode; definitions: KpiDefinition[] }
      >;
    }
  >();

  for (const definition of definitions) {
    const collectionNode = collectionNodeOf(definition);
    let collection = collections.get(collectionNode.code);
    if (!collection) {
      collection = { node: collectionNode, categories: new Map() };
      collections.set(collectionNode.code, collection);
    }

    const categoryNode = categoryNodeOf(definition);
    let category = collection.categories.get(categoryNode.code);
    if (!category) {
      category = { node: categoryNode, definitions: [] };
      collection.categories.set(categoryNode.code, category);
    }
    category.definitions.push(definition);
  }

  return [...collections.values()]
    .map((collection) => ({
      collection: collection.node,
      count: [...collection.categories.values()].reduce(
        (total, category) => total + category.definitions.length,
        0,
      ),
      categories: [...collection.categories.values()]
        .map((category) => ({
          category: category.node,
          definitions: [...category.definitions].sort(
            (a, b) =>
              a.name.localeCompare(b.name) || a.code.localeCompare(b.code),
          ),
        }))
        .sort((a, b) => compareTaxonomyNodes(a.category, b.category)),
    }))
    .sort((a, b) => compareTaxonomyNodes(a.collection, b.collection));
}
